// LocationRequest.tsx
import React, { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';

interface LocationCoordinates {
	latitude: number;
	longitude: number;
}

interface LocationRequestProps {
	onLocationReceived: (coordinates: LocationCoordinates) => void;
}

const LocationRequest: React.FC<LocationRequestProps> = ({ onLocationReceived }) => {
	const [showDialog, setShowDialog] = useState(false);
	const [permissionState, setPermissionState] = useState('unknown');
	const [browserInstructions, setBrowserInstructions] = useState<string>('');
	const t = useTranslations('Login');

	const requestLocation = async () => {
		try {
			const position: GeolocationPosition = await new Promise((resolve, reject) => {
				navigator.geolocation.getCurrentPosition(resolve, reject, {
					enableHighAccuracy: true,
					timeout: 40000,
					maximumAge: 0
				});
			});

			onLocationReceived({
				latitude: position.coords.latitude,
				longitude: position.coords.longitude
			});
			setShowDialog(false);
			setBrowserInstructions('');
		} catch (error) {
			console.error('Location error:', error);

			if (error instanceof GeolocationPositionError) {
				switch (error.code) {
					case error.PERMISSION_DENIED:
						setPermissionState('denied');
						setShowDialog(true);
						if ((window as Window & { chrome?: unknown }).chrome) {
							setBrowserInstructions(t('LocationChromeInstructions'));
						} else if (navigator.userAgent.includes('Safari')) {
							setBrowserInstructions(t('LocationSafariInstructions'));
						} else {
							setBrowserInstructions(t('LocationBrowserInstructions'));
						}
						break;
					case error.POSITION_UNAVAILABLE:
						setPermissionState('unavailable');
						setShowDialog(true);
						setBrowserInstructions('');
						break;
					case error.TIMEOUT:
						setPermissionState('timeout');
						setShowDialog(true);
						setBrowserInstructions('');
						break;
				}
			}
		}
	};

	const checkPermission = async () => {
		if (navigator.permissions && navigator.permissions.query) {
			try {
				const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
				setPermissionState(result.state);

				result.addEventListener('change', () => {
					setPermissionState(result.state);
					if (result.state === 'granted') {
						requestLocation();
					}
				});

				if (result.state === 'prompt' || result.state === 'granted') {
					requestLocation();
				}
			} catch (error) {
				console.error('Permission check error:', error);
				requestLocation();
			}
		} else {
			requestLocation();
		}
	};

	useEffect(() => {
		checkPermission();
	}, []);

	const getDialogContent = () => {
		switch (permissionState) {
			case 'denied':
				return {
					title: t('LocationAccessRequired'),
					description: t('LocationAccessDenied'),
					buttonText: t('TryAgain')
				};
			case 'unavailable':
				return {
					title: t('LocationUnavailable'),
					description: t('LocationUnavailableDesc'),
					buttonText: t('TryAgain')
				};
			case 'timeout':
				return {
					title: t('LocationTimeout'),
					description: t('LocationTimeoutDesc'),
					buttonText: t('Retry')
				};
			default:
				return {
					title: t('LocationAccessRequired'),
					description: t('LocationAccessNeeded'),
					buttonText: t('GrantAccess')
				};
		}
	};

	if (!showDialog) return null;

	const dialogContent = getDialogContent();

	return (
		<div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
			<div className="bg-white rounded-lg max-w-md w-full shadow-xl">
				<div className="p-6">
					<div className="mb-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-start">
							<svg
								className="w-5 h-5 mr-2 mt-1 flex-shrink-0"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
								/>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
								/>
							</svg>
							<span>{dialogContent.title}</span>
						</h3>
						<p className="text-gray-600 mb-2">{dialogContent.description}</p>
						{browserInstructions && (
							<div className="mt-4 p-4 bg-blue-50 text-blue-700 rounded-md text-sm">
								<p className="font-medium mb-1">{t('BrowserInstructions')}:</p>
								<p>{browserInstructions}</p>
							</div>
						)}
					</div>
					<div className="flex justify-end gap-2">
						<button
							onClick={() => {
								setShowDialog(false);
								setBrowserInstructions('');
							}}
							className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300"
						>
							{t('Cancel')}
						</button>
						<button
							onClick={requestLocation}
							className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
						>
							{dialogContent.buttonText}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default LocationRequest;
